import { useMemo } from 'react';
import { ChantierIcon } from '@/components/chantiers/ChantierIcon';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarAffectation } from '@/types/calendar';

interface ChantierInfo {
  id: string;
  nom_chantier: string;
  client?: string;
  pictogramme?: string;
  couleur?: string;
}

// Type pour les équipes uniques extraites des affectations
interface UniqueEquipe {
  id: string;
  nom: string;
  pictogramme?: string;
  couleur?: string;
}

interface PlanningBadgeGroupedProps {
  chantier?: ChantierInfo;
  memberCount: number;
  affectations: CalendarAffectation[];
  /** Mode compact : masque le nom de l'équipe (quand plusieurs chantiers le même jour) */
  compact?: boolean;
}

export function PlanningBadgeGrouped({ chantier, memberCount, affectations, compact = false }: PlanningBadgeGroupedProps) {
  // Couleurs par défaut si non définies
  const chantierCouleur = chantier?.couleur || '#3b82f6';
  const chantierPicto = chantier?.pictogramme || 'Home';

  // Détecter si c'est un chantier en retard (via les notes des affectations)
  const isRetard = affectations.some(aff => aff.notes?.startsWith('Retard chantier'));

  // Extraire les équipes uniques des affectations
  const uniqueEquipes = useMemo<UniqueEquipe[]>(() => {
    const equipesMap = new Map<string, UniqueEquipe>();

    affectations.forEach(aff => {
      if (aff.equipe && aff.equipe.id && !equipesMap.has(aff.equipe.id)) {
        equipesMap.set(aff.equipe.id, {
          id: aff.equipe.id,
          nom: aff.equipe.nom,
          pictogramme: aff.equipe.pictogramme,
          couleur: aff.equipe.couleur,
        });
      }
    });

    return Array.from(equipesMap.values());
  }, [affectations]);

  const hasEquipes = uniqueEquipes.length > 0;
  const singleEquipe = uniqueEquipes.length === 1;

  return (
    <div
      className={cn(
        'flex flex-col p-1.5 rounded-lg transition-all cursor-pointer relative overflow-hidden',
        'bg-card border-2 border-white/20 hover:border-primary/50'
      )}
    >
      {/* Bande diagonale orange pour les retards */}
      {isRetard && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, transparent 50%, #f97316 50%)',
          }}
        />
      )}

      {/* Ligne 1 : Chantier */}
      <div className="flex items-center gap-1.5">
        {/* Picto Chantier */}
        <div
          className="w-5 h-5 rounded flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${chantierCouleur}20` }}
        >
          <ChantierIcon
            pictogramme={chantierPicto}
            couleur={chantierCouleur}
            size="sm"
            showBackground={false}
          />
        </div>

        {/* Nom chantier */}
        <span className="text-xs font-medium truncate flex-1 max-w-[80px]">
          {chantier?.nom_chantier || 'Chantier'}
        </span>

        {/* Nombre de membres */}
        {memberCount > 1 && (
          <div className="flex items-center gap-0.5 text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-1 rounded">
            <Users className="h-3 w-3" />
            <span>{memberCount}</span>
          </div>
        )}
      </div>

      {/* Ligne 2 : Équipes (si présentes et pas en mode compact) */}
      {hasEquipes && !compact && (
        <div className="flex items-center gap-1 mt-1">
          {singleEquipe ? (
            // Une seule équipe : picto + nom
            <>
              <div
                className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${uniqueEquipes[0].couleur || '#f97316'}20` }}
              >
                <EquipeIcon
                  pictogramme={uniqueEquipes[0].pictogramme || 'Wrench'}
                  couleur={uniqueEquipes[0].couleur || '#f97316'}
                  size="sm"
                  showBackground={false}
                />
              </div>
              <span className="text-[10px] text-foreground font-medium truncate max-w-[70px]">
                {uniqueEquipes[0].nom}
              </span>
            </>
          ) : (
            // Plusieurs équipes : pictos côte à côte
            uniqueEquipes.slice(0, 4).map((equipe) => (
              <div
                key={equipe.id}
                className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${equipe.couleur || '#f97316'}20` }}
              >
                <EquipeIcon
                  pictogramme={equipe.pictogramme || 'Wrench'}
                  couleur={equipe.couleur || '#f97316'}
                  size="sm"
                  showBackground={false}
                />
              </div>
            ))
          )}
          {uniqueEquipes.length > 4 && (
            <span className="text-[10px] text-muted-foreground">
              +{uniqueEquipes.length - 4}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
